/**
 * The Try it panel: auth strip, request builder, safety gate, response viewer.
 *
 * Built with real DOM nodes rather than innerHTML, so no value a reader types or an API
 * returns is ever parsed as markup.
 */

import { describeToken, forgetToken, rememberToken, resolveToken, signInUrl } from './auth.ts';
import { asCurl, buildRequest, sendRequest, type RequestValues } from './request.ts';
import type { TryItParam, TryItSpec } from './spec.ts';

const GROUP_LABEL: Record<TryItParam['in'], string> = {
  path: 'Path',
  query: 'Query',
  header: 'Header',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function mountPanel(root: HTMLElement, spec: TryItSpec): void {
  const shell = root.querySelector<HTMLElement>('[data-tryit-shell]');
  const staticNote = root.querySelector<HTMLElement>('[data-tryit-static]');
  if (!shell) return;

  shell.textContent = '';
  shell.hidden = false;
  staticNote?.remove();

  const values: RequestValues = {
    params: Object.fromEntries(spec.params.map((p) => [p.name, p.example ?? ''])),
    enabled: Object.fromEntries(spec.params.map((p) => [p.name, p.required])),
    body: spec.body?.example ?? '',
  };

  let token: string | null = null;

  // ----------------------------------------------------------------- auth strip
  const auth = el('div', 'tryit-auth');
  const authDot = el('span', 'tryit-dot');
  authDot.setAttribute('aria-hidden', 'true');
  const authBody = el('div', 'tryit-auth-body');
  const authState = el('p', 'tryit-auth-state', 'Checking for a signed-in session…');
  const authScopes = el('div', 'tryit-scopes');
  authBody.append(authState, authScopes);
  const authActions = el('div', 'tryit-auth-actions');
  auth.append(authDot, authBody, authActions);

  const manual = el('form', 'tryit-manual');
  manual.hidden = true;
  const manualInput = el('input', 'tryit-input');
  manualInput.type = 'password';
  manualInput.placeholder = 'Paste a bearer token';
  manualInput.autocomplete = 'off';
  manualInput.spellcheck = false;
  const manualSave = el('button', 'btn btn-sm', 'Use token');
  manualSave.type = 'submit';
  const manualHint = el(
    'p',
    'tryit-hint',
    'Open app.livepush.io while signed in, then run copy(localStorage.getItem("access_token")) in DevTools. Treat it like a password.',
  );
  manual.append(manualInput, manualSave);

  /** Renders the granted scopes as chips, with `root` called out as full access. */
  function renderScopes(scopes: string[]): void {
    authScopes.textContent = '';
    if (!scopes.length) {
      authScopes.append(el('span', 'tryit-chip', 'scopes unknown'));
      return;
    }

    if (scopes.includes('root')) {
      // Worth shouting about: a root token bypasses every scope check, so the reader
      // is one Send away from doing anything their account can do.
      authScopes.append(el('span', 'tryit-chip is-warn', 'root'));
      authScopes.append(el('span', 'tryit-scope-note', 'full access to your account'));
      return;
    }

    // A long scope list would wrap the row into a block; show a few and keep the rest
    // on the title attribute.
    const shown = scopes.slice(0, 4);
    for (const scope of shown) authScopes.append(el('span', 'tryit-chip', scope));
    if (scopes.length > shown.length) {
      const more = el('span', 'tryit-chip is-muted', `+${scopes.length - shown.length}`);
      more.title = scopes.slice(shown.length).join(', ');
      authScopes.append(more);
    }
  }

  function renderAuth(): void {
    authActions.textContent = '';
    auth.classList.remove('is-ready', 'is-checking');

    if (token) {
      const detail = describeToken(token);
      auth.classList.add('is-ready');
      authState.textContent = detail?.name ? detail.name : 'Token ready';
      renderScopes(detail?.scopes ?? []);

      const signOut = el('button', 'tryit-link', 'Switch token');
      signOut.type = 'button';
      signOut.addEventListener('click', () => {
        forgetToken(spec);
        token = null;
        manual.hidden = false;
        manualHint.hidden = false;
        renderAuth();
        update();
        manualInput.focus();
      });
      authActions.append(signOut);
      manual.hidden = true;
      manualHint.hidden = true;
      return;
    }

    authScopes.textContent = '';
    authState.textContent = 'Not signed in';
    const signIn = el('a', 'btn btn-sm btn-primary', 'Sign in');
    signIn.href = signInUrl(spec);
    const retry = el('button', 'btn btn-sm btn-quiet', 'I signed in, re-check');
    retry.type = 'button';
    retry.addEventListener('click', () => void detectSession(true));
    const paste = el('button', 'tryit-link', 'Paste a token');
    paste.type = 'button';
    paste.addEventListener('click', () => {
      manual.hidden = !manual.hidden;
      manualHint.hidden = manual.hidden;
      if (!manual.hidden) manualInput.focus();
    });
    authScopes.append(
      el('span', 'tryit-scope-note', 'Sign in to Livepush, or paste a token, to send requests.'),
    );
    authActions.append(signIn, retry, paste);
  }

  manual.addEventListener('submit', (event) => {
    event.preventDefault();
    const value = manualInput.value.trim();
    if (!value) return;
    token = value;
    rememberToken(spec, value);
    manualInput.value = '';
    renderAuth();
    update();
  });

  async function detectSession(announce = false): Promise<void> {
    if (announce) {
      auth.classList.add('is-checking');
      authState.textContent = 'Checking…';
      authScopes.textContent = '';
    }
    const state = await resolveToken(spec);
    token = state.token;
    renderAuth();
    update();
  }

  // ------------------------------------------------------------------- builder
  const form = el('form', 'tryit-form');
  form.noValidate = true;
  const fields = el('div', 'tryit-fields');

  for (const group of ['path', 'query', 'header'] as const) {
    const inGroup = spec.params.filter((p) => p.in === group);
    if (!inGroup.length) continue;

    const fieldset = el('fieldset', 'tryit-group');
    const legend = el('legend', 'tryit-legend', `${GROUP_LABEL[group]} parameters`);
    fieldset.append(legend);

    for (const param of inGroup) {
      const row = el('div', 'tryit-row');
      const label = el('label', 'tryit-label');
      label.htmlFor = `tryit-${spec.operationId}-${param.name}`;

      if (!param.required) {
        const toggle = el('input', 'tryit-toggle');
        toggle.type = 'checkbox';
        toggle.checked = values.enabled[param.name] ?? false;
        toggle.addEventListener('change', () => {
          values.enabled[param.name] = toggle.checked;
          update();
        });
        label.append(toggle);
      }

      label.append(el('code', undefined, param.name));
      if (param.required) label.append(el('span', 'tryit-required', 'required'));
      row.append(label);

      let input: HTMLInputElement | HTMLSelectElement;
      if (param.enum?.length) {
        const select = el('select', 'tryit-input');
        for (const option of param.enum) {
          const node = el('option', undefined, option);
          node.value = option;
          select.append(node);
        }
        select.value = values.params[param.name] ?? param.enum[0] ?? '';
        input = select;
      } else {
        const text = el('input', 'tryit-input');
        text.type = 'text';
        text.value = values.params[param.name] ?? '';
        text.placeholder = param.example ?? param.type ?? 'value';
        text.autocomplete = 'off';
        text.spellcheck = false;
        input = text;
      }
      input.id = label.htmlFor;
      input.addEventListener('input', () => {
        values.params[param.name] = input.value;
        update();
      });
      input.addEventListener('change', () => {
        values.params[param.name] = input.value;
        update();
      });
      row.append(input);

      if (param.description) {
        row.append(el('p', 'tryit-hint', param.description));
      }
      fieldset.append(row);
    }
    fields.append(fieldset);
  }

  let bodyInput: HTMLTextAreaElement | null = null;
  if (spec.body) {
    const fieldset = el('fieldset', 'tryit-group');
    fieldset.append(el('legend', 'tryit-legend', `Request body (${spec.body.contentType})`));
    const area = el('textarea', 'tryit-input tryit-body');
    area.rows = Math.min(16, Math.max(4, spec.body.example.split('\n').length + 1));
    area.value = values.body;
    area.spellcheck = false;
    area.addEventListener('input', () => {
      values.body = area.value;
      update();
    });
    fieldset.append(area);
    const bodyError = el('p', 'tryit-hint tryit-error');
    bodyError.hidden = true;
    fieldset.append(bodyError);
    bodyInput = area;
    area.addEventListener('blur', () => {
      if (!area.value.trim()) {
        bodyError.hidden = true;
        return;
      }
      try {
        JSON.parse(area.value);
        bodyError.hidden = true;
      } catch (error) {
        bodyError.hidden = false;
        bodyError.textContent = `Not valid JSON: ${(error as Error).message}`;
      }
    });
    fields.append(fieldset);
  }

  // --------------------------------------------------------------- preview + gate
  const preview = el('pre', 'tryit-preview');
  const previewCode = el('code');
  preview.append(previewCode);

  const gate = el('div', 'tryit-gate');
  const gateNote = el('p', 'tryit-gate-note');
  const gateConfirm = el('input', 'tryit-input tryit-arm');
  gateConfirm.type = 'text';
  gateConfirm.autocomplete = 'off';
  gateConfirm.spellcheck = false;
  gate.append(gateNote);
  if (spec.risk === 'destructive') gate.append(gateConfirm);

  const actions = el('div', 'tryit-actions');
  const send = el('button', 'btn btn-primary', 'Send request');
  send.type = 'submit';
  const copy = el('button', 'btn btn-quiet', 'Copy as curl');
  copy.type = 'button';
  const status = el('p', 'tryit-status');
  actions.append(send, copy, status);

  // ------------------------------------------------------------------- response
  const result = el('div', 'tryit-result');
  result.hidden = true;

  form.append(fields, gate, preview, actions);
  shell.append(auth, manual, manualHint, form, result);
  manualHint.hidden = true;

  /** The id a destructive call must have typed back to arm it. */
  function armTarget(): string {
    const pathParams = spec.params.filter((p) => p.in === 'path');
    const last = pathParams[pathParams.length - 1];
    return last ? (values.params[last.name] ?? '').trim() : '';
  }

  function update(): void {
    const request = buildRequest(spec, values, token);
    previewCode.textContent = `${request.method} ${request.url}`;

    const needsToken = spec.requiresAuth && !token;
    const armed =
      spec.risk !== 'destructive' || (armTarget() !== '' && gateConfirm.value.trim() === armTarget());

    if (spec.risk === 'destructive') {
      const target = armTarget();
      gateConfirm.placeholder = target || 'the id above';
      gateNote.textContent = target
        ? `This permanently deletes data. Type ${target} to arm the request.`
        : 'This permanently deletes data. Fill in the path parameter above first.';
      gate.classList.add('is-destructive');
    } else if (spec.risk === 'mutating') {
      gateNote.textContent = 'This changes live data on your account.';
      gate.classList.add('is-mutating');
    } else {
      gateNote.textContent = 'This only reads data.';
    }

    send.disabled = needsToken || request.missing.length > 0 || !armed;
    status.textContent = needsToken
      ? 'Sign in or paste a token to send.'
      : request.missing.length
        ? `Fill in: ${request.missing.join(', ')}`
        : '';
  }

  gateConfirm.addEventListener('input', update);

  copy.addEventListener('click', async () => {
    const request = buildRequest(spec, values, token);
    try {
      await navigator.clipboard.writeText(asCurl(request, 'YOUR_API_TOKEN'));
      copy.textContent = 'Copied';
      setTimeout(() => (copy.textContent = 'Copy as curl'), 1600);
    } catch {
      status.textContent = 'Could not reach the clipboard.';
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (send.disabled) return;

    if (spec.risk === 'mutating' && !window.confirm(`${spec.method} ${spec.path}\n\nThis changes live data on your account. Continue?`)) {
      return;
    }

    if (bodyInput && bodyInput.value.trim()) {
      try {
        JSON.parse(bodyInput.value);
      } catch (error) {
        status.textContent = `Request body is not valid JSON: ${(error as Error).message}`;
        return;
      }
    }

    send.disabled = true;
    send.textContent = 'Sending…';
    status.textContent = '';

    const request = buildRequest(spec, values, token);
    const response = await sendRequest(request);

    send.textContent = 'Send request';
    renderResult(response);
    update();

    // A 401 almost always means the stored token went stale; drop it so the next
    // attempt re-reads the cookie instead of failing the same way again.
    if (response.status === 401) {
      forgetToken(spec);
      token = null;
      renderAuth();
      update();
    }
  });

  function renderResult(response: Awaited<ReturnType<typeof sendRequest>>): void {
    result.hidden = false;
    result.textContent = '';

    const head = el('div', 'tryit-result-head');
    const code = el(
      'span',
      `tryit-code ${response.ok ? 'is-ok' : response.status === 0 ? 'is-fail' : 'is-error'}`,
      response.status === 0 ? 'failed' : `${response.status} ${response.statusText}`,
    );
    head.append(code, el('span', 'tryit-meta', `${response.durationMs} ms · ${response.bytes} B`));
    result.append(head);

    if (response.networkError) {
      const note = el('p', 'callout is-warning');
      note.textContent =
        'The request never reached the server. The usual cause is that this docs origin is ' +
        'not yet in the API host’s CORS allowlist; being offline or blocking the request ' +
        'in an extension looks identical to the browser. The curl above will work regardless.';
      result.append(note);
      return;
    }

    const bodyPre = el('pre', 'tryit-response');
    const bodyCode = el('code');
    bodyCode.textContent =
      response.json !== undefined ? JSON.stringify(response.json, null, 2) : response.text || '(empty body)';
    bodyPre.append(bodyCode);
    result.append(bodyPre);

    if (response.headers.length) {
      const details = el('details', 'tryit-headers');
      details.append(el('summary', undefined, `Response headers (${response.headers.length})`));
      const list = el('pre');
      list.textContent = response.headers.map(([k, v]) => `${k}: ${v}`).join('\n');
      details.append(list);
      result.append(details);
    }
  }

  renderAuth();
  update();
  void detectSession();
}
